import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import SuperFetch from 'library/helpers/superFetch';
import { approvalActions } from './slice';
import { classifyDecide, classifyRetry } from './decideOutcome';

// Prefer the server's own words. `info` is where a 403/404 explains itself; `error` is what
// SuperFetch attaches on a transport failure, and it may be an Error, a string, or an object —
// String()-ing that last shape onto the screen reads as "[object Object]".
const errMsg = (res, fallback) =>
	res?.info || res?.message || (typeof res?.error === 'string' ? res.error : res?.error?.message) || fallback;

/** GET /approval-requests?status=… → the caller-scoped queue. */
export function* getApprovals(action) {
	try {
		const status = action?.payload?.status ?? undefined;
		const res = yield call(() => SuperFetch.get('/approval-requests', {}, status ? { status } : {}));
		if (res.statusCode === 200) {
			yield put(approvalActions.loadApprovalsSuccess(res.data || []));
		} else {
			throw Error(errMsg(res, 'Failed to load approval requests.'));
		}
	} catch (error) {
		yield put(approvalActions.loadApprovalsError(error.message || String(error)));
	}
}

/**
 * POST /approval-requests/:id/approve | /reject.
 *
 * 🛑 A NON-200 IS NOT AN EXCEPTION HERE. The server answers a failed decide with a 409 whose body
 * says WHAT happened — refused, in flight, already decided, outcome unknown. Throwing on it would
 * discard exactly the information the operator needs, so every response is classified and settled
 * through one action. The only `catch` is for a transport failure, where there is genuinely no
 * answer to read.
 */
export function* decideApproval(action) {
	const { approvalRequestID, decision, decisionReason } = action.payload;
	const path = `/approval-requests/${approvalRequestID}/${decision === 'rejected' ? 'reject' : 'approve'}`;

	/**
	 * 🛑 NO try/catch AROUND SuperFetch. It never rejects — superFetch.js catches its own fetch and
	 * RETURNS `{ errors: 'Server Error', error }`, so a catch here is dead code that reads like a
	 * safety net. An earlier version had one, and its careful "the outcome is unknown, do not retry"
	 * message was unreachable: a dropped connection fell through to a plain error toast that faded,
	 * and the operator retried the decide the whole contract exists to keep them from retrying.
	 *
	 * A lost reply is now classified where every other outcome is classified — classifyDecide's
	 * isTransportFailure — so there is ONE place that decides what a response means.
	 */
	const res = yield call(() => SuperFetch.post(path, decisionReason ? { decisionReason } : {}));
	yield put(
		approvalActions.decideApprovalSettled({ approvalRequestID, outcome: classifyDecide(res, { decision }) })
	);

	// Re-read on ANY settled attempt, not just success. A refusal often means the row moved
	// underneath us (someone else decided it, it expired, it is mid-apply) — leaving the stale
	// row on screen under an Approve button is how a second decision gets attempted.
	yield put(approvalActions.loadApprovals({ status: action.payload.statusFilter ?? undefined }));
}

/** POST /approval-requests/:id/retry — re-open a failed_apply row. */
export function* retryApproval(action) {
	const { approvalRequestID, verificationNote } = action.payload;
	// Same reasoning as decideApproval: SuperFetch resolves on failure, so classifyRetry sees the
	// transport failure and reports it as one.
	const res = yield call(() =>
		SuperFetch.post(`/approval-requests/${approvalRequestID}/retry`, { verificationNote })
	);
	yield put(approvalActions.decideApprovalSettled({ approvalRequestID, outcome: classifyRetry(res) }));
	// Re-read on BOTH arms, as decide does. A failed retry usually means the row is not in the state
	// the operator thinks it is, which is precisely when a stale row on screen does harm.
	yield put(approvalActions.loadApprovals({ status: action.payload.statusFilter ?? undefined }));
}

export default function* approvalsSaga() {
	yield all([
		takeLatest(approvalActions.loadApprovals.type, getApprovals),
		// takeEvery, NOT takeLatest: two decisions on DIFFERENT rows are independent, and cancelling
		// the first would leave its row spinning forever with its outcome never settled.
		takeEvery(approvalActions.decideApproval.type, decideApproval),
		takeEvery(approvalActions.retryApproval.type, retryApproval)
	]);
}
